import React from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, ChevronRight, Settings } from 'lucide-react';
import Header from '../components/Layout/Header';
import { doctors } from '../data/mockData';

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 80px;
  overflow-y: auto;
`;

const Content = styled.div`
  padding: 0;
`;

const ProfileHeader = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 32px 20px;
  text-align: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const DoctorImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  overflow: hidden;
  margin: 0 auto 16px;
  background: linear-gradient(135deg, #C8F5D8 0%, #33A9FF 100%);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const DoctorName = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px 0;
`;

const DoctorSpecialty = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 8px 0;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const DoctorExperience = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const Section = styled.section`
  background: ${({ theme }) => theme.colors.white};
  padding: 24px 20px;
  margin-bottom: 8px;
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 16px 0;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0;
`;

const ScheduleCard = styled.div`
  background: ${({ theme }) => theme.colors.gray};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 16px;
  margin-bottom: 16px;
`;

const ScheduleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ScheduleLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ScheduleValue = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const ManageButton = styled.button`
  width: 100%;
  padding: 12px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
`;

const ConsultItem = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
    margin: 0 -20px;
    padding: 16px 20px;
  }
`;

const ConsultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
`;

const ConsultPatient = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const ConsultDate = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ConsultType = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const ServicesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ServiceItem = styled.div`
  background: ${({ theme }) => theme.colors.gray};
  padding: 12px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const PerfilMedico = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const doctor = doctors.find(d => d.id === parseInt(id)) || doctors[0];

  return (
    <PageContainer>
      <Header title="Perfil del Médico" showBack />
      
      <Content>
        <ProfileHeader>
          <DoctorImage>
            <img src={doctor.image} alt={doctor.name} />
          </DoctorImage>
          <DoctorName>{doctor.name}</DoctorName>
          <DoctorSpecialty>{doctor.specialty}</DoctorSpecialty>
          <DoctorExperience>{doctor.experience}</DoctorExperience>
        </ProfileHeader>

        <Section>
          <Description>{doctor.description}</Description>
        </Section>

        <Section>
          <SectionTitle>Agenda de Hoy</SectionTitle>
          <ScheduleCard>
            <ScheduleRow>
              <ScheduleLabel>Citas pendientes:</ScheduleLabel>
              <ScheduleValue>{doctor.pendingAppointments}</ScheduleValue>
            </ScheduleRow>
            <ScheduleRow>
              <ScheduleLabel>Próxima disponibilidad:</ScheduleLabel>
              <ScheduleValue>{doctor.nextAvailable}</ScheduleValue>
            </ScheduleRow>
          </ScheduleCard>
          <ManageButton onClick={() => navigate(`/gestionar-agenda/${doctor.id}`)}>
            <Calendar />
            Gestionar Agenda
          </ManageButton>
        </Section>

        <Section>
          <SectionTitle>Historial de Consultas</SectionTitle>
          {doctor.consultHistory && doctor.consultHistory.length > 0 ? (
            doctor.consultHistory.map((consult, index) => (
              <ConsultItem key={index}>
                <ConsultHeader>
                  <ConsultPatient>{consult.patient}</ConsultPatient>
                  <ChevronRight size={20} color="#6C757D" />
                </ConsultHeader>
                <ConsultDate>{consult.date} - {consult.type}</ConsultDate>
              </ConsultItem>
            ))
          ) : (
            <Description>No hay consultas registradas.</Description>
          )}
        </Section>

        <Section>
          <SectionTitle>Servicios Ofrecidos</SectionTitle>
          <ServicesList>
            {doctor.services && doctor.services.map((service, index) => (
              <ServiceItem key={index}>{service}</ServiceItem>
            ))}
          </ServicesList>
          <ManageButton onClick={() => navigate(`/gestion-servicios/${doctor.id}`)} style={{ marginTop: '16px' }}>
            <Settings />
            Gestionar Servicios
          </ManageButton>
        </Section>
      </Content>
    </PageContainer>
  );
};

export default PerfilMedico;
